import React, { useState, useRef, useTransition } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { trackEvent } from '../lib/posthog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Search, RefreshCw, Download, Filter, X, RotateCcw } from 'lucide-react';
import { SettingsPanel } from './SettingsPanel';
import { DateFilterBar } from './DateFilterBar';

interface FilterState {
  search: string;
  dateRange: string;
  status: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  minSpend?: number;
  minImpressions?: number;
  adStatus?: string;
  includeDynamicFeed?: boolean;
  onlyXyzManaged?: boolean;
  customStartDate?: string;
  customEndDate?: string;
}

interface DashboardControlsProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onRefresh: () => void;
  onExportCSV: () => void;
  isLoading?: boolean;
  totalCount?: number;
  filteredCount?: number;
  showAdvancedFilters?: boolean;
}

const SORT_OPTIONS = [
  { value: 'roas', label: 'ROAS' },
  { value: 'cost', label: 'Cost' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'ctr', label: 'CTR' },
  { value: 'cpc', label: 'CPC' },
  { value: 'reach', label: 'Reach' },
  { value: 'title', label: 'Title' }
];



const AD_STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Status' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'PENDING_REVIEW', label: 'Pending Review' },
  { value: 'DISAPPROVED', label: 'Disapproved' },
  { value: 'PREAPPROVED', label: 'Preapproved' },
  { value: 'ARCHIVED', label: 'Archived' }
];

export function DashboardControls({
  filters,
  onFiltersChange,
  onRefresh,
  onExportCSV,
  isLoading = false,
  totalCount = 0,
  filteredCount = 0,
  showAdvancedFilters = true
}: DashboardControlsProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [, startTransition] = useTransition();

  // Track search with debounce to avoid flooding analytics
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Handler for preset selection from DateFilterBar
  const handlePresetSelect = (preset: string) => {
    onFiltersChange({
      ...filters,
      dateRange: preset,
      customStartDate: undefined,
      customEndDate: undefined
    });
    trackEvent('dashboard_date_range_changed', { date_range: preset });
  };

  // Handler for custom range from DateFilterBar
  const handleCustomRangeQuery = (startDate: string, endDate: string) => {
    trackEvent('dashboard_custom_query_run', {
      start_date: startDate,
      end_date: endDate,
    });
    onFiltersChange({
      ...filters,
      dateRange: 'custom',
      customStartDate: startDate,
      customEndDate: endDate
    });
  };

  const updateFilter = (key: keyof FilterState, value: any) => {
    startTransition(() => {
      onFiltersChange({
        ...filters,
        [key]: value
      });
    });

    // Track filter changes
    if (key === 'dateRange' && value !== 'custom') {
      trackEvent('dashboard_date_range_changed', { date_range: value });
    } else if (key === 'sortBy') {
      trackEvent('dashboard_sort_changed', { sort_by: value, sort_order: filters.sortOrder });
    } else if (key === 'sortOrder') {
      trackEvent('dashboard_sort_changed', { sort_by: filters.sortBy, sort_order: value });
    } else if (key === 'search') {
      // Debounce search tracking to avoid too many events
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        if (value) {
          trackEvent('dashboard_search_performed', { search_length: value.length });
        }
      }, 1000);
    }
  };

  const clearFilters = () => {
    trackEvent('dashboard_filters_reset');
    onFiltersChange({
      search: '',
      dateRange: 'last_week_sun_sat', // Always reset to last_week_sun_sat as default
      status: 'ACTIVE',
      sortBy: 'roas',
      sortOrder: 'desc',
      minSpend: 0.01, // Default: hide zero-spend ads
      adStatus: 'ACTIVE'
    });
  };

  const hasActiveFilters = filters.search ||
    (filters.minSpend && filters.minSpend > 0.01) ||
    filters.minImpressions ||
    filters.dateRange !== 'last_week_sun_sat' ||
    filters.status !== 'ACTIVE' ||
    (filters.adStatus && filters.adStatus !== 'ACTIVE');

  return (
    <div className="flex items-center justify-between gap-4">
      {/* Left Side - DateFilterBar */}
      <div className="flex items-center gap-2">
        <DateFilterBar
          selectedDateRange={filters.dateRange}
          customStartDate={filters.customStartDate}
          customEndDate={filters.customEndDate}
          onPresetSelect={handlePresetSelect}
          onCustomRangeQuery={handleCustomRangeQuery}
        />
        {filteredCount !== totalCount && (
          <Badge variant="secondary">
            {filteredCount.toLocaleString()} of {totalCount.toLocaleString()}
          </Badge>
        )}
      </div>

      {/* Right Side - Controls */}
      <div className="flex items-center gap-2">
        {/* Search & Advanced Filters Popover */}
        {showAdvancedFilters && <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[600px]" align="end">
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Search & Filters</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsFilterOpen(false)}
                  className="h-6 w-6 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Search & Sort Row */}
              <div className="grid grid-cols-4 gap-3 p-3 bg-muted/50 rounded-lg items-end">
                {/* Search */}
                <div className="col-span-2">
                  <Label htmlFor="search" className="text-sm">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Search titles or ad names..."
                      value={filters.search}
                      onChange={(e) => updateFilter('search', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Sort By */}
                <div>
                  <Label htmlFor="sortBy" className="text-sm">Sort By</Label>
                  <Select value={filters.sortBy} onValueChange={(value) => updateFilter('sortBy', value)}>
                    <SelectTrigger id="sortBy">
                      <SelectValue placeholder="ROAS" />
                    </SelectTrigger>
                    <SelectContent>
                      {SORT_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Sort Order */}
                <div>
                  <Label htmlFor="sortOrder" className="text-sm">Order</Label>
                  <Select value={filters.sortOrder} onValueChange={(value: 'asc' | 'desc') => updateFilter('sortOrder', value)}>
                    <SelectTrigger id="sortOrder">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">High to Low</SelectItem>
                      <SelectItem value="asc">Low to High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Advanced Filters */}
              <div className="grid grid-cols-5 gap-3 p-3 bg-muted/30 rounded-lg">
                <div>
                  <Label htmlFor="minSpend" className="text-sm">Min Spend ($)</Label>
                  <Input
                    id="minSpend"
                    type="number"
                    placeholder="0"
                    value={filters.minSpend || ''}
                    onChange={(e) => updateFilter('minSpend', e.target.value ? Number(e.target.value) : undefined)}
                  />
                </div>

                <div>
                  <Label htmlFor="minImpressions" className="text-sm">Min Impressions</Label>
                  <Input
                    id="minImpressions"
                    type="number"
                    placeholder="0"
                    value={filters.minImpressions || ''}
                    onChange={(e) => updateFilter('minImpressions', e.target.value ? Number(e.target.value) : undefined)}
                  />
                </div>

                <div>
                  <Label htmlFor="status" className="text-sm">Campaign Status</Label>
                  <Select value={filters.status} onValueChange={(value) => updateFilter('status', value)}>
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="PAUSED">Paused</SelectItem>
                      <SelectItem value="ALL">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="adStatus" className="text-sm">Ad Status</Label>
                  <Select
                    value={filters.adStatus || 'ACTIVE'}
                    onValueChange={(value) => updateFilter('adStatus', value)}
                  >
                    <SelectTrigger id="adStatus">
                      <SelectValue placeholder="Active" />
                    </SelectTrigger>
                    <SelectContent>
                      {AD_STATUS_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Dynamic Feed Filter */}
                <div>
                  <Label className="text-sm">Options</Label>
                  <div className="flex items-center space-x-2 mt-2">
                    <Checkbox
                      id="includeDynamicFeed"
                      checked={filters.includeDynamicFeed || false}
                      onCheckedChange={(checked) => updateFilter('includeDynamicFeed', checked)}
                    />
                    <Label htmlFor="includeDynamicFeed" className="text-xs">
                      Include Dynamic Feed
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 mt-2">
                    <Checkbox
                      id="excludeNewAds"
                      checked={filters.excludeNewAds || false}
                      onCheckedChange={(checked) => updateFilter('excludeNewAds', checked)}
                    />
                    <Label htmlFor="excludeNewAds" className="text-xs">
                      Hide New Ads
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 mt-2">
                    <Checkbox
                      id="onlyXyzManaged"
                      checked={filters.onlyXyzManaged ?? true}
                      onCheckedChange={(checked) => updateFilter('onlyXyzManaged', checked)}
                    />
                    <Label htmlFor="onlyXyzManaged" className="text-xs">
                      XYZ Managed Only
                    </Label>
                  </div>
                </div>
              </div>

              {/* Reset Filters Button */}
              {hasActiveFilters && (
                <div className="pt-2 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="w-full"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset All Filters
                  </Button>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>}
        
        <SettingsPanel onDataRefresh={onRefresh} />
        
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={onExportCSV}
          disabled={isLoading || filteredCount === 0}
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>
    </div>
  );
}
